import { injectable } from 'inversify';
import { Message, Field, Type } from "protobufjs/light";
import { Result, ok } from '@xlearn/common'
import * as Api from '@/app/state-serialization/api'
import { SectionState } from '../common'
import { SectionStatus } from '../api'
import { ProtobufSerializer } from '@/app/state-serialization/infrastructure';

@injectable()
export class SectionStateProtobufSerializer implements Api.StateSerializationPlugin<SectionState[]> {

  key = { id: 2, name: { ns: 'suspend-data', name: 'sections' }, format: 'protobuf' }

  serialize(data: SectionState[]): Result<Uint8Array, Api.SerializerError> {

    const sections = data.map(section => {
      const details = section.details
      const progress = details.progress
      
      return new SectionStateProtoMessage({
        id: section.id,
        status: mapStatus(details.status),
        progress: new SectionStateProgressProtoMessage({
          completion: Math.round(fixNumber(progress.completion)),
          page: Math.round(fixNumber(progress.page)),
          scroll: Math.round(fixNumber(progress.scroll))
        }),
        durationSeconds: Math.round(details.durationSeconds)
      })
    })

    const message = new SectionStatesProtoMessage({
      sections: sections
    })

    return ProtobufSerializer.serialize<SectionStatesProtoMessage>(message, SectionStatesProtoMessage)
    
    function mapStatus(input: SectionStatus) {
      switch (input) {
        case SectionStatus.notAttempted:
          return SectionStatusProtoEnum.notAttempted
        case SectionStatus.incomplete:
          return SectionStatusProtoEnum.incomplete
        case SectionStatus.completed:
          return SectionStatusProtoEnum.completed
      }
    }

    function fixNumber(num: number) {
      if (isNaN(num)) {
        return 0
      }

      return num
    }
  }

  deserialize(content: Uint8Array): Result<SectionState[], Api.DeserializerError> {

    const deserializeResult = ProtobufSerializer.deserialize<SectionStatesProtoMessage>(content,
                                                                                        SectionStatesProtoMessage)
    if (!deserializeResult.success) {
      return deserializeResult
    }

    const sections = deserializeResult.value.sections.map(section => {
      const progress = section.progress
      const result: SectionState = {
        id: section.id,
        details: {
          status: mapStatus(section.status),
          progress: {
            completion: progress.completion,
            page: progress.page,
            scroll: progress.scroll
          },
          durationSeconds: section.durationSeconds
        }
      }

      return result
    })

    return ok(sections)

    function mapStatus(input: SectionStatusProtoEnum) {
      switch (input) {
        case SectionStatusProtoEnum.notAttempted:
          return SectionStatus.notAttempted
        case SectionStatusProtoEnum.incomplete:
          return SectionStatus.incomplete
        case SectionStatusProtoEnum.completed:
          return SectionStatus.completed
      }
    }
  }


}

enum SectionStatusProtoEnum {
  notAttempted = 0,
  incomplete = 1,
  completed = 2
}

@Type.d('SectionStateProgressProtoMessage')
class SectionStateProgressProtoMessage extends Message<SectionStateProgressProtoMessage> {

  @Field.d(1, 'uint32', "optional", 0)
  completion: number;
  @Field.d(2, 'uint32', "optional", 0)
  scroll: number;
  @Field.d(3, 'uint32', "optional", 0)
  page: number; 
}

@Type.d('SectionStateProtoMessage')
class SectionStateProtoMessage extends Message<SectionStateProtoMessage> {

  @Field.d(1, 'string', "optional", '')
  id: string
  
  @Field.d(2, SectionStatusProtoEnum, "optional", SectionStatusProtoEnum.incomplete)
  status: SectionStatusProtoEnum;

  @Field.d(3, SectionStateProgressProtoMessage)
  progress: SectionStateProgressProtoMessage;
  
  @Field.d(4, 'uint32', "optional", 0)
  durationSeconds: number;
}

@Type.d('SectionStatesProtoMessage')
class SectionStatesProtoMessage extends Message<SectionStatesProtoMessage> {

  @Field.d(1, 'uint32', 'optional', 1)
  public schemaVersion: number

  @Field.d(2, SectionStateProtoMessage, 'repeated')
  public sections: SectionStateProtoMessage[]
}
